import type { Lang } from "../../shared/i18n";
import type { CameraState } from "./settingsModel";
import type { PlayMode } from "../../shared/stores/playPreference";

/**
 * G1–G4's copy.
 *
 * 🔴 G4'S SENTENCES ARE A LEGAL STATEMENT, NOT MARKETING. Invariant I1 is what
 * keeps a camera pointed at children simple — COPPA's amended Rule classifies
 * face templates as personal information — and a parent or a principal reading
 * this screen is checking a claim. Every line has to be literally true of the
 * code: nothing face- or body-derived is transmitted, persisted or logged, not
 * for debugging and not behind a flag.
 *
 * 🔴 NO PERCENTAGE AND NO EMOJI, the same rules the dashboard tiers carry.
 */

interface SettingsDict {
  title: string;
  /** E6, and only the school tier draws it — see `settingsTabsFor`. */
  tabSchool: string;
  tabLanguage: string;
  tabAccessibility: string;
  tabCamera: string;

  /* G2 */
  languageTitle: string;
  languageWhy: string;
  languageScope: string;

  /* G3 */
  motionTitle: string;
  motionToggle: string;
  motionWhy: string;
  motionOsNote: string;
  playModeTitle: string;
  playModeWhy: string;
  playMode: Record<PlayMode, string>;
  playModeSameReward: string;

  /* G4 */
  cameraTitle: string;
  cameraState: Record<CameraState, string>;
  cameraFallback: string;
  privacyTitle: string;
  privacyPoints: readonly string[];
  privacyRecording: string;

  /* ── change 19 §7 — the school-wide recording switch ─────────────────── */
  recordingTitle: string;
  recordingToggle: string;
  recordingHint: string;
  recordingConsentFloor: string;
  recordingLoadFailed: string;
  recordingSaveFailed: string;
}

export const SETTINGS_COPY: Record<Lang, SettingsDict> = {
  en: {
    title: "Settings",
    tabSchool: "School",
    tabLanguage: "Language",
    tabAccessibility: "Accessibility",
    tabCamera: "Camera & privacy",

    /* 🔴 The same sentence the school-admin credential panel uses, for the same
       reason: this response body is the only copy that will ever exist. */
    /* ⚠️ It asks for a code for the same reason the password form asks for the
       current password: a session alone is a laptop left open in a staff room. */

    languageTitle: "Language",
    languageWhy: "Everything an adult reads, in one language or the other.",
    /** ⚠️ Wholesale, never per-string — the same rule as the content itself. */
    languageScope:
      "This changes the whole interface. It does not change what a child hears in a game.",

    motionTitle: "Movement on screen",
    motionToggle: "Reduce animation",
    /* Not a preference. Vestibular sensitivity is real, and some children find
       motion distressing (design-system.md §1). */
    motionWhy:
      "Rewards and transitions fade instead of springing. Nothing is removed, and no feedback waits on an animation either way.",
    motionOsNote:
      "If your device is already set to reduce motion, that applies on its own — this switch can only add to it, never override it.",

    playModeTitle: "How to play",
    /* 🔴 A choice, not a fallback — playPreference.ts's whole argument. */
    playModeWhy:
      "A child can jump, raise a hand, or tap. Pick whatever suits the room, the body and the day — it can be changed at any time.",
    playMode: {
      jump: "Jump and move",
      point: "Raise a hand",
      tap: "Tap the screen",
    },
    /** 🔴 The honesty that makes offering the choice acceptable at all. */
    playModeSameReward:
      "The game is exactly as hard either way, and the same stars and coins are earned. Tapping records no physical-movement progress, because none happened.",

    cameraTitle: "Camera",
    cameraState: {
      granted: "This browser may use the camera",
      denied: "This browser is blocking the camera",
      prompt: "Kalqy will ask before using the camera",
      unsupported: "This device has no camera Kalqy can use",
    },
    /** 🔴 I4 — an unusable camera is never a failure the child is shown. */
    cameraFallback:
      "Every game can be finished without a camera. If it cannot be used, the child is simply given the tapping version — they are never shown an error and never blocked.",

    privacyTitle: "What leaves this device",
    /* 🔴 I1, in sentences somebody can check against the code. */
    privacyPoints: [
      "Nothing from the camera leaves this device. No picture, no video, no frame.",
      "The measurements the game makes from the picture — where a hand or a shoulder is — are used on this device and then thrown away. They are never sent, never saved and never written to a log.",
      /* 🔴 THIS LINE USED TO SAY "there is no video or audio recording feature
         anywhere in Kalqy, at any tier, behind any setting", AND IT HAD STOPPED
         BEING TRUE. `recordings.service.ts` opens by saying so in as many words:
         *"this feature reverses what privacy-compliance.md promised."* Leaving
         the old sentence on a teacher's privacy screen was the worst version of
         the reversal — the product doing the thing and telling them it could
         not. What replaces it is the same guarantee stated as it actually
         stands: no audio ever, and video only where a parent has said yes. */
      "There is no audio recording anywhere in Kalqy — no microphone is ever opened. Video of a session is recorded only where that child’s parent has consented and the school has switched it on; with either missing, nothing is captured.",
      "What is sent is what happened in the game: which question, whether it was answered, when. Never how a child looked.",
    ],
    privacyRecording:
      'When Kalqy says a session is "recorded" without a camera involved, it means that list of game events and nothing else.',

    /* ── change 19 §7 ─────────────────────────────────────────────────────── */
    recordingTitle: "Session recording, school-wide",
    recordingToggle: "Allow session recording at this school",
    /* ⚠️ THE HINT SAYS WHAT TURNING IT **OFF** DOES, because that is the half
       with immediate consequences for every class at once. */
    recordingHint:
      "Off means nothing is ever recorded here, for any child, in any class — whatever a parent has agreed to. It is off unless you turn it on.",
    /* 🔴 THE SENTENCE THAT STOPS THIS READING AS A MASTER SWITCH. */
    recordingConsentFloor:
      "Turning it on does not start recording anyone. A child is recorded only if their own parent has separately given consent, and a parent can withdraw that at any time. Changes apply from the next session, not one already running.",
    recordingLoadFailed: "Couldn’t load this school’s recording setting.",
    recordingSaveFailed:
      "That did not save. The setting is unchanged — check the connection and try again.",
  },
};
